import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Lock, LogOut, Users, TrendingUp, CalendarDays, Download, Share2, ArrowLeft, Loader2, BarChart3, FlaskConical } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sb-admin-token";

export default function AdminDashboard() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [input, setInput] = useState("");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (tk) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/subscribers/stats`, { params: { token: tk }, timeout: 15000 });
      setStats(res.data);
      setToken(tk);
      sessionStorage.setItem(TOKEN_KEY, tk);
    } catch (e) {
      if (e.response && e.response.status === 403) setError("Wrong access token. Try again.");
      else setError("Couldn't load stats. Please try again.");
      setStats(null);
      sessionStorage.removeItem(TOKEN_KEY);
      setToken("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setStats(null);
    setInput("");
  };

  // ---- Gate ----
  if (!token || !stats) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-6">
        <div className="w-full max-w-sm rounded-[2rem] bg-slate-900 border border-white/10 p-8 shadow-2xl">
          <div className="w-14 h-14 rounded-2xl bg-teal-500/15 text-teal-400 flex items-center justify-center mx-auto mb-5">
            <Lock className="w-7 h-7" />
          </div>
          <h1 className="font-display font-800 text-2xl text-white text-center">Signups Dashboard</h1>
          <p className="text-white/50 text-sm text-center mt-2 mb-6">Enter your admin access token to continue.</p>
          <form onSubmit={(e) => { e.preventDefault(); if (input.trim()) load(input.trim()); }} className="space-y-3">
            <Input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Access token"
              data-testid="admin-token-input"
              className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
            {error && <p data-testid="admin-error" className="text-sm font-semibold text-rose-400">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="admin-login-button" className="w-full h-12 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-bold">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Unlock dashboard"}
            </Button>
          </form>
          <Link to="/" className="mt-5 flex items-center justify-center gap-2 text-sm text-white/40 hover:text-white/70">
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
        </div>
      </div>
    );
  }

  const maxDay = Math.max(1, ...stats.per_day.map((d) => d.count));
  const maxSource = Math.max(1, ...stats.by_source.map((s) => s.count));

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display font-800 text-3xl">Signups Dashboard</h1>
            <p className="text-white/40 text-sm mt-1">Sharkbowl Games newsletter performance</p>
          </div>
          <div className="flex items-center gap-3">
            <a href={`${API}/subscribers/export?token=${encodeURIComponent(token)}`}>
              <Button data-testid="admin-export-button" className="rounded-full bg-teal-600 hover:bg-teal-700 text-white font-bold gap-2">
                <Download className="w-4 h-4" /> Export CSV
              </Button>
            </a>
            <Button onClick={logout} variant="outline" data-testid="admin-logout-button" className="rounded-full border-white/15 text-white/80 hover:bg-white/5 gap-2">
              <LogOut className="w-4 h-4" /> Log out
            </Button>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
          <StatCard icon={Users} label="Total subscribers" value={stats.total} accent="text-teal-400" bg="bg-teal-500/10" testid="stat-total" />
          <StatCard icon={CalendarDays} label="Today" value={stats.today} accent="text-cyan-400" bg="bg-cyan-500/10" testid="stat-today" />
          <StatCard icon={TrendingUp} label="Last 7 days" value={stats.last7} accent="text-orange-400" bg="bg-orange-500/10" testid="stat-last7" />
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          {/* Per-day chart */}
          <Panel title="Signups (last 14 days)" icon={BarChart3}>
            {stats.per_day.length === 0 ? (
              <Empty text="No signups yet." />
            ) : (
              <div className="flex items-end gap-1.5 h-40 mt-2">
                {stats.per_day.map((d) => (
                  <div key={d.date} className="flex-1 flex flex-col items-center gap-1.5 group" title={`${d.date}: ${d.count}`}>
                    <div className="w-full bg-gradient-to-t from-teal-500 to-cyan-400 rounded-t-md transition-all" style={{ height: `${(d.count / maxDay) * 100}%`, minHeight: d.count > 0 ? "6px" : "2px" }} />
                    <span className="text-[9px] text-white/30 rotate-0">{d.date.slice(5)}</span>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          {/* Source breakdown */}
          <Panel title="Where they signed up" icon={Share2}>
            {stats.by_source.length === 0 ? (
              <Empty text="No data yet." />
            ) : (
              <div className="space-y-3 mt-2" data-testid="source-breakdown">
                {stats.by_source.map((s) => (
                  <div key={s.source}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white/70 font-medium">{prettySource(s.source)}</span>
                      <span className="text-white/50">{s.count}</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-teal-500 to-cyan-400 rounded-full" style={{ width: `${(s.count / maxSource) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          {/* Top referrers */}
          <Panel title="Top referrers" icon={TrendingUp}>
            {stats.top_referrers.length === 0 ? (
              <Empty text="No referrals yet. Share links via the reward step start counting here." />
            ) : (
              <ul className="space-y-2 mt-2" data-testid="top-referrers">
                {stats.top_referrers.map((r, i) => (
                  <li key={r.ref_code} className="flex items-center justify-between text-sm bg-white/5 rounded-xl px-3 py-2">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="w-5 text-center font-800 font-display text-teal-400">{i + 1}</span>
                      <span className="truncate text-white/80">{r.email}</span>
                    </span>
                    <span className="font-800 font-display text-cyan-300">{r.referrals} ref</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          {/* Recent signups */}
          <Panel title="Recent signups" icon={Users}>
            {stats.recent.length === 0 ? (
              <Empty text="No signups yet." />
            ) : (
              <ul className="divide-y divide-white/5 mt-1" data-testid="recent-signups">
                {stats.recent.map((r, i) => (
                  <li key={i} className="flex items-center justify-between py-2 text-sm">
                    <span className="truncate text-white/80 mr-3">{r.email}</span>
                    <span className="text-white/40 text-xs shrink-0">{prettySource(r.source)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>

        {/* A/B tests */}
        <div className="mt-6" data-testid="ab-tests-section">
          <Panel title="A/B tests" icon={FlaskConical}>
            {(!stats.ab_tests || stats.ab_tests.length === 0) ? (
              <Empty text="No A/B data yet. The popup headline test starts collecting as visitors see it." />
            ) : (
              <div className="space-y-6 mt-2">
                {stats.ab_tests.map((t) => {
                  const best = t.variants.reduce((a, b) => (b.rate > (a ? a.rate : -1) ? b : a), null);
                  return (
                    <div key={t.test}>
                      <p className="text-white/70 font-semibold mb-3">{prettyTest(t.test)}</p>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {t.variants.map((v) => {
                          const isWinner = best && v.variant === best.variant && v.conversions > 0;
                          return (
                            <div key={v.variant} className={`rounded-xl p-4 border ${isWinner ? "border-teal-400/60 bg-teal-500/10" : "border-white/10 bg-white/5"}`}>
                              <div className="flex items-center justify-between">
                                <span className="font-display font-800 text-white">Variant {v.variant}</span>
                                {isWinner && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-teal-500 text-white">Winning</span>}
                              </div>
                              <div className="text-xs text-white/40 mt-1 mb-2">"{headlineLabel(t.test, v.variant)}"</div>
                              <div className="flex items-end justify-between">
                                <div>
                                  <div className="font-display font-800 text-2xl text-teal-300">{v.rate}%</div>
                                  <div className="text-white/40 text-xs">conversion</div>
                                </div>
                                <div className="text-right text-xs text-white/50">
                                  <div>{v.conversions} signups</div>
                                  <div>{v.impressions} views</div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent, bg, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl bg-slate-900 border border-white/10 p-5 flex items-center gap-4">
      <span className={`w-12 h-12 rounded-xl ${bg} ${accent} flex items-center justify-center`}>
        <Icon className="w-6 h-6" />
      </span>
      <div>
        <div className="font-display font-800 text-3xl">{value}</div>
        <div className="text-white/40 text-sm">{label}</div>
      </div>
    </div>
  );
}

function Panel({ title, icon: Icon, children }) {
  return (
    <div className="rounded-2xl bg-slate-900 border border-white/10 p-5">
      <h3 className="flex items-center gap-2 font-display font-700 text-lg mb-2">
        <Icon className="w-5 h-5 text-teal-400" /> {title}
      </h3>
      {children}
    </div>
  );
}

function Empty({ text }) {
  return <p className="text-white/30 text-sm py-6 text-center">{text}</p>;
}

function prettySource(s) {
  if (!s) return "Unknown";
  const map = {
    "newsletter-section": "Newsletter section",
    "exit-popup": "Exit/scroll popup",
    "game-over": "Bubble Pop game",
    "tournament": "Launch-day tournament",
    "contact": "Contact form",
  };
  if (map[s]) return map[s];
  if (s.startsWith("coming-soon:")) return `Coming soon (${s.split(":")[1]})`;
  return s;
}

function prettyTest(t) {
  return t === "popup_headline" ? "Popup headline" : t;
}

function headlineLabel(test, variant) {
  if (test === "popup_headline") {
    return { A: "Be first to dive in.", B: "Get early access + free wallpapers." }[variant] || variant;
  }
  return variant;
}
